/**
 * StoneDevelopmentCard - Stone Investment themed Development/Project Card
 * 
 * A distinctive card for property developments (projects) with Stone Investment's
 * luxury portfolio aesthetic. Differentiated from StoneListingCard with:
 * - Vertical/editorial layout emphasis
 * - Progress indicators for units sold
 * - Timeline visualisation
 * - Marketing status badges
 * - Development stage indicators
 * 
 * Uses the same design language (greyscale palette, Cormorant Garamond + Montserrat,
 * square corners, HorizonOverlay) but with a more editorial feel.
 */

'use client';

import { ArrowUpRight, Building2, Calendar, ChevronLeft, ChevronRight, Home, Layers, MapPin } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useCallback, useRef, useState } from 'react';
import { detectRegion } from '../utils/regionColors';
import { HorizonOverlay } from './PropertyCard';

// ============================================================================
// Types
// ============================================================================

export type ProjectStatus = 
  | 'ACTIVE'
  | 'UPCOMING'
  | 'ON_HOLD'
  | 'SOLD_OUT'
  | 'COMPLETED_ARCHIVED'
  | 'CANCELLED'
  | 'OTHER';

export type ProjectStage = 
  | 'PLANNING'
  | 'DESIGN'
  | 'PRE_CONSTRUCTION_APPROVALS'
  | 'PRE_LAUNCH_MARKETING'
  | 'LAUNCH_SALES'
  | 'UNDER_CONSTRUCTION'
  | 'CONSTRUCTION_COMPLETED'
  | 'SETTLEMENT_PHASE'
  | 'POST_COMPLETION_SALES'
  | 'OTHER';

export type ProjectType = 
  | 'APARTMENTS'
  | 'TOWNHOUSES'
  | 'LAND_SUBDIVISION'
  | 'HOUSE_AND_LAND_PACKAGES'
  | 'MIXED_USE'
  | 'COMMERCIAL_OFFICE'
  | 'COMMERCIAL_RETAIL'
  | 'COMMERCIAL_INDUSTRIAL'
  | 'COMMERCIAL_STRATA'
  | 'OTHER';

export interface DevelopmentCardData {
  id: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  status?: ProjectStatus | null;
  stage?: ProjectStage | null;
  projectType?: ProjectType | null;
  
  // Unit Information
  totalUnits?: number | null;
  soldUnits?: number | null;
  availableUnits?: number | null;
  unitMixSummary?: string | null;
  
  // Timeline
  estimatedStartDate?: string | null;
  estimatedCompletionDate?: string | null;
  actualCompletionDate?: string | null;
  
  // Display Information
  displayPriceText?: string | null;
  marketingStatusText?: string | null;
  heroImageUrl?: string | null;
  imageUrls?: string[] | undefined;
  
  // Location
  location?: {
    name?: string | null;
    suburb?: string | null;
    city?: string | null;
    district?: string | null;
    neighbourhood?: string | null;
    region?: string | null;
    country?: string | null;
    countryCode?: string | null;
  } | null;
  
  // Organisation names (when included)
  developer?: { id: string; name: string } | null;
  builder?: { id: string; name: string } | null;
  architect?: { id: string; name: string } | null;
}

export interface StoneDevelopmentCardProps {
  development: DevelopmentCardData;
  href: string;
  variant?: 'default' | 'compact' | 'featured' | 'editorial';
  showUnitsProgress?: boolean;
  showTimeline?: boolean;
  showDeveloper?: boolean;
  locale?: string;
  className?: string;
  messages?: Record<string, unknown> | undefined;
}

// ============================================================================
// Utility Functions
// ============================================================================

function getProjectStatusLabel(status: ProjectStatus | null | undefined, statusMessages?: Record<string, string>): string {
  const labels: Record<ProjectStatus, string> = {
    ACTIVE: statusMessages?.nowSelling || 'Now Selling',
    UPCOMING: statusMessages?.comingSoon || 'Coming Soon',
    ON_HOLD: statusMessages?.onHold || 'On Hold',
    SOLD_OUT: statusMessages?.soldOut || 'Sold Out',
    COMPLETED_ARCHIVED: statusMessages?.completed || 'Completed',
    CANCELLED: statusMessages?.cancelled || 'Cancelled',
    OTHER: statusMessages?.available || 'Available',
  };
  return status ? labels[status] || (statusMessages?.available || 'Available') : (statusMessages?.available || 'Available');
}

function getProjectStatusColor(status: ProjectStatus | null | undefined): string {
  const colors: Record<ProjectStatus, string> = {
    ACTIVE: 'bg-stone-900 text-stone-100',
    UPCOMING: 'bg-stone-700 text-stone-100',
    ON_HOLD: 'bg-stone-500 text-stone-100',
    SOLD_OUT: 'bg-stone-900 text-white',
    COMPLETED_ARCHIVED: 'bg-stone-600 text-stone-100',
    CANCELLED: 'bg-stone-400 text-stone-900',
    OTHER: 'bg-stone-500 text-stone-100',
  };
  return status ? colors[status] || 'bg-stone-500 text-stone-100' : 'bg-stone-500 text-stone-100';
}

function getProjectStageLabel(stage: ProjectStage | null | undefined, stageMessages?: Record<string, string>): string {
  const labels: Record<ProjectStage, string> = {
    PLANNING: stageMessages?.planning || 'Planning',
    DESIGN: stageMessages?.designPhase || 'Design Phase',
    PRE_CONSTRUCTION_APPROVALS: stageMessages?.approvals || 'Approvals',
    PRE_LAUNCH_MARKETING: stageMessages?.preLaunch || 'Pre-Launch',
    LAUNCH_SALES: stageMessages?.launching || 'Launching',
    UNDER_CONSTRUCTION: stageMessages?.underConstruction || 'Under Construction',
    CONSTRUCTION_COMPLETED: stageMessages?.constructionComplete || 'Construction Complete',
    SETTLEMENT_PHASE: stageMessages?.settlement || 'Settlement',
    POST_COMPLETION_SALES: stageMessages?.finalRelease || 'Final Release',
    OTHER: '',
  };
  return stage ? labels[stage] || '' : '';
}

function getProjectTypeLabel(type: ProjectType | null | undefined, typeMessages?: Record<string, string>): string {
  const labels: Record<ProjectType, string> = {
    APARTMENTS: typeMessages?.apartments || 'Apartments',
    TOWNHOUSES: typeMessages?.townhouses || 'Townhouses',
    LAND_SUBDIVISION: typeMessages?.land || 'Land',
    HOUSE_AND_LAND_PACKAGES: typeMessages?.houseAndLand || 'House & Land',
    MIXED_USE: typeMessages?.mixedUse || 'Mixed Use',
    COMMERCIAL_OFFICE: typeMessages?.commercialOffice || 'Commercial Office',
    COMMERCIAL_RETAIL: typeMessages?.retail || 'Retail',
    COMMERCIAL_INDUSTRIAL: typeMessages?.industrial || 'Industrial',
    COMMERCIAL_STRATA: typeMessages?.strata || 'Strata',
    OTHER: typeMessages?.development || 'Development',
  };
  return type ? labels[type] || (typeMessages?.development || 'Development') : (typeMessages?.development || 'Development');
}

function getProjectTypeIcon(type: ProjectType | null | undefined) {
  switch (type) {
    case 'APARTMENTS':
      return Building2;
    case 'TOWNHOUSES':
    case 'HOUSE_AND_LAND_PACKAGES':
      return Home;
    case 'LAND_SUBDIVISION':
      return Layers;
    default:
      return Building2;
  }
}

// ============================================================================
// Component
// ============================================================================

export const StoneDevelopmentCard = memo(function StoneDevelopmentCard({
  development,
  href,
  variant = 'default',
  showUnitsProgress: _showUnitsProgress = true,
  showTimeline = true,
  showDeveloper = false,
  locale = 'en',
  className = '',
  messages,
}: StoneDevelopmentCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  
  // Get stone-specific translations
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const devMessages = (stoneMessages.development || {}) as Record<string, unknown>;
  const statusMessages = (devMessages.status || {}) as Record<string, string>;
  const stageMessages = (devMessages.stage || {}) as Record<string, string>;
  const typeMessages = (devMessages.type || {}) as Record<string, string>;
  const listingMessages = (stoneMessages.listing || {}) as Record<string, string>;
  
  const t = (key: string, fallback: string): string => {
    return (devMessages[key] as string) || fallback;
  };
  
  const {
    name,
    description,
    status,
    stage,
    projectType,
    unitMixSummary,
    estimatedCompletionDate,
    actualCompletionDate,
    displayPriceText,
    marketingStatusText,
    heroImageUrl,
    imageUrls,
    location,
    developer,
  } = development;

  const images = imageUrls?.length ? imageUrls : heroImageUrl ? [heroImageUrl] : [];
  const imageCount = images.length;
  const hasMultipleImages = imageCount > 1;

  const goToImage = useCallback(
    (idx: number) => setImageIndex(((idx % imageCount) + imageCount) % imageCount),
    [imageCount],
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]!.clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const delta = e.changedTouches[0]!.clientX - touchStartX.current;
      if (Math.abs(delta) > 50) {
        e.preventDefault();
        goToImage(imageIndex + (delta < 0 ? 1 : -1));
      }
      touchStartX.current = null;
    },
    [goToImage, imageIndex],
  );

  // Build location string for region detection
  const locationString = [
    location?.suburb,
    location?.city,
    location?.region,
    location?.country,
    location?.countryCode,
  ].filter(Boolean).join(', ');

  // Display location (cleaner format)
  const displayLocation = [
    location?.suburb,
    location?.city,
    location?.district,
    location?.neighbourhood,
    location?.region || location?.country,
  ].filter(Boolean).join(', ') || location?.name || (listingMessages.locationTBA || 'Location TBA');

  // Detect region for flag colours, using structured location data first
  const locationCountry = location?.country || '';
  const locationCountryCode = (location?.countryCode || '').toLowerCase();
  const isFrance = locationCountryCode === 'fr'
    || locationCountry.toLowerCase() === 'france'
    || (!locationCountry && !locationCountryCode && detectRegion(locationString) === 'provence');
  const countryLabel = locationCountry
    || (locationCountryCode === 'fr' ? 'France' : locationCountryCode === 'mu' ? 'Mauritius' : '')
    || (isFrance ? 'France' : '');

  // Determine completion date to show
  const completionDate = actualCompletionDate || estimatedCompletionDate;
  const isEstimated = !actualCompletionDate && !!estimatedCompletionDate;

  // Get project type icon
  const TypeIcon = getProjectTypeIcon(projectType);

  // Aspect ratio based on variant
  const aspectRatio = variant === 'editorial' ? 'aspect-[4/5]' : variant === 'compact' ? 'aspect-[16/10]' : 'aspect-[4/3]';

  return (
    <Link href={href} className="block no-underline text-inherit">
      <article
        className={`group relative w-full cursor-pointer flex flex-col ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image Container */}
        <div
          className={`relative w-full ${aspectRatio} overflow-hidden bg-stone-200`}
          onTouchStart={hasMultipleImages ? handleTouchStart : undefined}
          onTouchEnd={hasMultipleImages ? handleTouchEnd : undefined}
        >
          {/* Images with crossfade */}
          {images.length > 0 ? (
            images.map((url, i) => {
              const isActive = i === imageIndex;
              return (
                <div
                  key={url}
                  className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                    isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                >
                  <Image
                    src={url}
                    alt={`${name} ${i + 1}`}
                    fill
                    loading={i === 0 ? 'eager' : 'lazy'}
                    className={`object-cover transition-transform duration-[2000ms] ease-out will-change-transform ${
                      isHovered && isActive ? 'scale-105' : 'scale-100'
                    }`}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </div>
              );
            })
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-stone-300 to-stone-200 flex items-center justify-center">
              <Building2 size={48} className="text-stone-400" strokeWidth={1} />
            </div>
          )}

          {/* Carousel navigation arrows (hover-only) */}
          {hasMultipleImages && (
            <>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToImage(imageIndex - 1);
                }}
                className={`absolute left-3 top-1/2 -translate-y-1/2 z-30 w-8 h-8 flex items-center justify-center bg-black/25 backdrop-blur-sm hover:bg-black/40 transition-all duration-300 ${
                  isHovered ? 'opacity-100' : 'opacity-0'
                }`}
                aria-label="Previous image"
              >
                <ChevronLeft size={16} className="text-white" strokeWidth={1.5} />
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToImage(imageIndex + 1);
                }}
                className={`absolute right-3 top-1/2 -translate-y-1/2 z-30 w-8 h-8 flex items-center justify-center bg-black/25 backdrop-blur-sm hover:bg-black/40 transition-all duration-300 ${
                  isHovered ? 'opacity-100' : 'opacity-0'
                }`}
                aria-label="Next image"
              >
                <ChevronRight size={16} className="text-white" strokeWidth={1.5} />
              </button>

              {/* Dot indicators */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex gap-1.5">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      goToImage(i);
                    }}
                    className={`h-1.5 transition-all duration-500 ${
                      i === imageIndex
                        ? 'bg-white w-4'
                        : 'bg-white/40 hover:bg-white/60 w-1.5'
                    }`}
                    aria-label={`Go to image ${i + 1}`}
                  />
                ))}
              </div>

              {/* Image counter (hover-only) */}
              <div
                className={`absolute top-6 right-6 z-30 px-2 py-1 bg-black/30 backdrop-blur-sm text-white text-[10px] font-sans tracking-wider transition-opacity duration-300 ${
                  isHovered ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {imageIndex + 1} / {imageCount}
              </div>
            </>
          )}

          {/* HorizonOverlay with flag colours */}
          <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />

          {/* Center Floating "Explore" Button */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 flex items-center justify-center pointer-events-none">
            <div 
              className={`relative flex items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] ${
                isHovered ? 'w-28 h-28 opacity-100' : 'w-14 h-14 opacity-0 translate-y-4'
              }`}
            >
              <div className="absolute inset-0 border-[0.5px] border-white/60 rounded-full animate-[spin_12s_linear_infinite]" />
              <span className="text-white font-serif italic text-lg tracking-widest drop-shadow-md">
                {t('explore', 'Explore')}
              </span>
            </div>
          </div>

          {/* Location Label - Bottom Left */}
          <div className="absolute bottom-6 left-6 z-20 overflow-hidden">
            <div 
              className={`flex items-center gap-2 transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                isHovered ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: '100ms' }}
            >
              <MapPin size={12} className="text-stone-200" strokeWidth={1.5} />
              <p className="text-[10px] uppercase tracking-[0.3em] text-stone-200 font-sans font-medium">
                {displayLocation}
              </p>
            </div>
          </div>

          {/* Project Type Badge - Top Left */}
          <div className="absolute top-6 left-6 flex items-center gap-2">
            <div className="px-3 py-1.5 bg-white/95 text-[10px] tracking-[0.2em] uppercase font-sans text-stone-900 flex items-center gap-2 backdrop-blur-sm">
              <TypeIcon size={12} strokeWidth={1.5} />
              {getProjectTypeLabel(projectType, typeMessages)}
            </div>
          </div>

          {/* Marketing Status or Status Badge - Top Right (shifted down when counter shown) */}
          {(marketingStatusText || status) && (
            <div className={`absolute ${hasMultipleImages ? 'top-12' : 'top-6'} right-6 z-20`}>
              {marketingStatusText ? (
                <div className="px-3 py-1.5 bg-stone-900 text-[10px] tracking-[0.2em] uppercase font-sans text-white">
                  {marketingStatusText}
                </div>
              ) : (
                <div className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-sans ${getProjectStatusColor(status)}`}>
                  {getProjectStatusLabel(status, statusMessages)}
                </div>
              )}
            </div>
          )}

          {/* Units Available Counter - Hidden per client request */}
        </div>

        {/* Content Section - Editorial Style */}
        <div className="flex flex-col gap-4 pt-6">
          {/* Stage Badge */}
          {stage && stage !== 'OTHER' && (
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-stone-400 rounded-full" />
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-sans">
                {getProjectStageLabel(stage, stageMessages)}
              </p>
            </div>
          )}

          {/* Title */}
          <h3 className={`font-serif text-2xl md:text-3xl text-stone-900 leading-tight transition-all duration-500 ${
            isHovered ? 'italic' : ''
          }`}>
            {name}
          </h3>

          {/* Description (if compact variant or featured) */}
          {description && (variant === 'featured' || variant === 'editorial') && (
            <p className="text-sm text-stone-600 font-sans line-clamp-2 leading-relaxed">
              {description}
            </p>
          )}

          {/* Divider */}
          <div className="border-t border-stone-200" />

          {/* Metadata Row */}
          <div className="flex flex-wrap items-center gap-4 text-stone-600 font-sans text-sm tracking-wider">
            {/* Unit Mix Summary */}
            {unitMixSummary && (
              <>
                <span className="flex items-center gap-1.5">
                  <Home size={14} className="text-stone-400" strokeWidth={1.5} />
                  <span className="text-stone-900 font-medium">{unitMixSummary}</span>
                </span>
                <span className="w-px h-3 bg-stone-300" />
              </>
            )}

            {/* Total Units - Hidden per client request */}

            {/* Completion Date */}
            {showTimeline && completionDate && (
              <span className="flex items-center gap-1.5">
                <Calendar size={14} className="text-stone-400" strokeWidth={1.5} />
                {isEstimated ? (t('estimated', 'Est.') + ' ') : ''}
                {new Date(completionDate).toLocaleDateString(locale, {
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            )}

            {/* Region Indicator */}
            {countryLabel && (
              <span className={`ml-auto transition-colors duration-500 uppercase text-xs tracking-[0.2em] ${
                isFrance ? 'group-hover:text-blue-900' : 'group-hover:text-teal-800'
              }`}>
                {countryLabel}
              </span>
            )}
          </div>

          {/* Units Progress Bar - Hidden per client request */}

          {/* Price and Developer Row */}
          <div className="flex items-end justify-between pt-2">
            {/* Price */}
            <div>
              {displayPriceText ? (
                <p className="font-serif text-xl text-stone-900">{displayPriceText}</p>
              ) : (
                <p className="font-sans text-sm text-stone-500 italic">{t('priceOnEnquiry', 'Price on enquiry')}</p>
              )}
            </div>

            {/* Developer */}
            {showDeveloper && developer && (
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.15em] text-stone-400 font-sans">{t('developer', 'Developer')}</p>
                <p className="text-sm text-stone-700 font-sans">{developer.name}</p>
              </div>
            )}

            {/* Arrow indicator */}
            {!showDeveloper && (
              <div className={`transition-all duration-500 ${
                isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}>
                <ArrowUpRight size={24} className="text-stone-900" strokeWidth={1} />
              </div>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
});

export default StoneDevelopmentCard;
